import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/aiApi';
import { contentKeys } from '../../media-library/hooks/useCustomContent';
import type { CreateAiGenerationRequest } from '../types';

export const aiKeys = {
  all: ['ai-generation'] as const,
  voices: (language: string) => [...aiKeys.all, 'voices', language] as const,
  suggestions: (clipType: string, language: string) =>
    [...aiKeys.all, 'suggestions', clipType, language] as const,
  quota: () => [...aiKeys.all, 'quota'] as const,
  job: (jobId: string) => [...aiKeys.all, 'job', jobId] as const,
};

export function useAiVoices(language: string) {
  return useQuery({
    queryKey: aiKeys.voices(language),
    queryFn: () => api.getVoices(language),
    staleTime: 5 * 60_000,
  });
}

export function useAiSuggestions(clipType: string, language: string, enabled = true) {
  return useQuery({
    queryKey: aiKeys.suggestions(clipType, language),
    queryFn: () => api.getSuggestions(clipType, language),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useAiQuota() {
  return useQuery({ queryKey: aiKeys.quota(), queryFn: api.getQuota });
}

/** Polls a generation job every 3s while pending/processing; stops when done/failed. */
export function useAiJob(jobId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.job(jobId ?? ''),
    queryFn: () => api.pollGenerationStatus(jobId!),
    enabled: !!jobId && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'processing' ? 3000 : false;
    },
  });
}

export function useSubmitGeneration() {
  return useMutation({ mutationFn: (req: CreateAiGenerationRequest) => api.submitGeneration(req) });
}

export function useSaveClip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.saveGeneratedClip(jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contentKeys.lists() });
      qc.invalidateQueries({ queryKey: contentKeys.quota() });
      qc.invalidateQueries({ queryKey: aiKeys.quota() });
    },
  });
}

export function useDiscardGeneration() {
  return useMutation({ mutationFn: (jobId: string) => api.discardGeneration(jobId) });
}
